// App-level shared state: reader settings + TTS settings. Lives in zustand
// so any subtree can read/update without prop-drilling. We persist on every
// change via settingsService (debounced).

import { create } from "zustand";
import {
  defaultReaderSettings,
  type ReaderSettings,
  type ReaderTheme,
} from "@/types/reader";
import { defaultTTSSettings, type TTSSettings } from "@/types/tts";
import {
  loadReaderSettings,
  loadTTSSettings,
  saveReaderSettings,
  saveTTSSettings,
} from "@/services/settingsService";

/** Sleep-timer state. `expiresAt` is a wall-clock ms epoch; the reader
 *  ticks a countdown from it and, at expiry, runs a 30 s volume fade
 *  then pauses playback. `durationMinutes` is preserved so we can label
 *  the pill and re-arm the same duration if the user taps "add 5 min". */
export interface SleepTimerState {
  expiresAt: number | null;
  durationMinutes: number | null;
}

interface ReaderStoreState {
  reader: ReaderSettings;
  tts: TTSSettings;
  loaded: boolean;
  sleepTimer: SleepTimerState;
  init: () => Promise<void>;
  setReader: (patch: Partial<ReaderSettings>) => void;
  setTheme: (theme: ReaderTheme) => void;
  setTTS: (patch: Partial<TTSSettings>) => void;
  armSleepTimer: (minutes: number | null) => void;
}

let saveTimer: number | undefined;
const saveSoon = (kind: "reader" | "tts", value: unknown) => {
  if (saveTimer) window.clearTimeout(saveTimer);
  saveTimer = window.setTimeout(() => {
    if (kind === "reader") saveReaderSettings(value as ReaderSettings);
    else saveTTSSettings(value as TTSSettings);
  }, 200);
};

export const useReaderStore = create<ReaderStoreState>((set, get) => ({
  reader: defaultReaderSettings,
  tts: defaultTTSSettings,
  loaded: false,
  sleepTimer: { expiresAt: null, durationMinutes: null },
  async init() {
    if (get().loaded) return;
    const [reader, tts] = await Promise.all([
      loadReaderSettings(),
      loadTTSSettings(),
    ]);
    set({ reader, tts, loaded: true });
  },
  setReader(patch) {
    const next = { ...get().reader, ...patch };
    set({ reader: next });
    saveSoon("reader", next);
  },
  setTheme(theme) {
    const next = { ...get().reader, theme };
    set({ reader: next });
    saveSoon("reader", next);
  },
  setTTS(patch) {
    const next = { ...get().tts, ...patch };
    set({ tts: next });
    saveSoon("tts", next);
  },
  armSleepTimer(minutes) {
    if (minutes == null || minutes <= 0) {
      set({ sleepTimer: { expiresAt: null, durationMinutes: null } });
      return;
    }
    set({
      sleepTimer: {
        expiresAt: Date.now() + minutes * 60_000,
        durationMinutes: minutes,
      },
    });
  },
}));
